import React, { useMemo, useRef, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { useOrg } from '../../../components/OrgContext';
import { useAuth } from '../../../components/AuthContext';
import { useToast } from '../../../components/ToastContext';
import { logAudit } from '../../../hooks/useAudit';
import Dialog from '../../../components/Dialog';
import { IconDoc } from '../../../components/dialogIcons';
import { WAREHOUSE_FIELDS, flatten } from './WarehouseFields';
import { buildIndex, buildPlan, summarise } from './warehousePlan';
import PageLoading from '../../../components/PageLoading';

// Reading a warehouse file back in.
//
// It takes two kinds of file without being told which: the one Download
// template produces, and the raw Warehouse export straight out of Claimsoft.
// The Claimsoft headings are listed as aliases in WarehouseFields, so nothing
// has to be renamed by hand first.
//
// Nothing is written until you have seen what will happen. The preview says how
// many rows are new, how many change, and - the part that matters - every name
// it could not find, so a bad spelling gets caught now rather than becoming a
// missing link nobody notices for a month.
//
// The deciding is all in warehousePlan.js. It is kept apart from the screen
// because it is the part that can quietly get a thousand rows wrong, and code
// that is plain functions over plain data can be run against a real export and
// checked. Writing the rows is here; working out what to write is there.

export default function WarehouseImport({ onClose, onDone, regions, referrals, people, warehouses }) {
  const { profile } = useAuth();
  const { current } = useOrg();
  const toast = useToast();
  const fileRef = useRef(null);

  const [fileName, setFileName] = useState('');
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const index = useMemo(
    () => buildIndex({ regions, referrals, people, warehouses }),
    [regions, referrals, people, warehouses],
  );

  async function pickFile(file) {
    setError(''); setPlan(null);
    if (!file) return;
    setFileName(file.name);
    setBusy(true);
    try {
      const { default: Papa } = await import('papaparse');
      const text = await file.text();
      const parsed = Papa.parse(text, { header: true, skipEmptyLines: 'greedy' });
      if (!parsed.data?.length) { setError('That file has no rows in it.'); setBusy(false); return; }

      const made = buildPlan(parsed.data, parsed.meta.fields || [], index);
      if (made.error === 'no-name-column') {
        setError('This file has no warehouse name column. It needs one called \u201cWarehouse Name\u201d. Download the template to see the full set.');
      } else {
        setPlan(made);
      }
    } catch (e) {
      setError('Could not read that file: ' + e.message);
    }
    setBusy(false);
  }

  async function apply() {
    if (!plan) return;
    setBusy(true);
    const failures = [];
    let made = 0, changed = 0;

    // Anything that needs a link to another warehouse waits for pass two.
    const needsHub = [];

    for (const row of plan.creates) {
      const { data, error: e } = await supabase.from('warehouses').insert(row.values).select('id').single();
      if (e) { failures.push(row.name + ': ' + tidy(e.message)); continue; }
      made++;
      if (row.hubName)  needsHub.push({ id: data.id, name: row.name, hubName: row.hubName });
      if (row.assigned) await setAssigned(data.id, row.assigned, failures, row.name);
    }

    for (const row of plan.updates) {
      if (row.changes.length) {
        const { error: e } = await supabase.from('warehouses').update(row.values).eq('id', row.id);
        if (e) { failures.push(row.name + ': ' + tidy(e.message)); continue; }
        changed++;
      }
      if (row.hubName)  needsHub.push({ id: row.id, name: row.name, hubName: row.hubName });
      if (row.assigned) await setAssigned(row.id, row.assigned, failures, row.name);
    }

    // -- Pass two: the primary warehouse links --
    // Read the warehouse list back, so hubs created a moment ago count.
    let hubsLinked = 0, hubsMissing = 0;
    if (needsHub.length) {
      const { data: all } = await supabase.from('warehouses').select('id, name, warehouse_number');
      const byName = new Map();
      (all || []).forEach(w => {
        byName.set(flatten(w.name), w.id);
        if (w.warehouse_number) byName.set(flatten(w.warehouse_number), w.id);
      });
      for (const h of needsHub) {
        const hubId = byName.get(flatten(h.hubName));
        if (!hubId || hubId === h.id) { hubsMissing++; continue; }
        const { error: e } = await supabase.from('warehouses')
          .update({ primary_warehouse_id: hubId }).eq('id', h.id);
        if (e) failures.push(h.name + ' (primary warehouse): ' + tidy(e.message));
        else hubsLinked++;
      }
    }

    await logAudit(profile, 'WAREHOUSES_IMPORTED', 'warehouse', {
      target_name: fileName,
      details: { created: made, updated: changed, hubs: hubsLinked, failed: failures.length },
    });

    setBusy(false);
    if (failures.length) {
      toast.error(`${made} added, ${changed} updated. ${failures.length} row${failures.length === 1 ? '' : 's'} failed. First: ${failures[0]}`);
    } else {
      const tail = hubsMissing ? ` (${hubsMissing} primary warehouse names not found)` : '';
      toast.success(`${made} added, ${changed} updated${tail}`);
    }
    onDone();
  }

  const tidy = m =>
    m?.includes('warehouses_one_referral_source') ? 'that referral source is already paired with another warehouse'
    : m?.includes('warehouses_one_number')        ? 'another warehouse already uses that number'
    : m;

  async function setAssigned(warehouseId, userIds, failures, label) {
    const { data: existing } = await supabase
      .from('user_warehouses').select('user_id').eq('warehouse_id', warehouseId);
    const before = new Set((existing || []).map(r => r.user_id));
    const after  = new Set(userIds);
    const add    = userIds.filter(u => !before.has(u));
    const remove = [...before].filter(u => !after.has(u));
    if (add.length) {
      const { error: e } = await supabase.from('user_warehouses')
        .insert(add.map(user_id => ({ user_id, warehouse_id: warehouseId })));
      if (e) failures.push(label + ' (assigning people): ' + e.message);
    }
    if (remove.length) {
      const { error: e } = await supabase.from('user_warehouses')
        .delete().eq('warehouse_id', warehouseId).in('user_id', remove);
      if (e) failures.push(label + ' (unassigning people): ' + e.message);
    }
  }

  const nothingToDo = plan && !plan.creates.length && !plan.updates.length;

  /*
    **The last dialog in OnHand still on its own shell, moved 7 September 2026.**
    Rule 34 says every dialog is one shell, and Group E's count of eighteen
    missed this one because it lives on a settings screen rather than on the
    counting screen. Measuring the built app found it: below 1024 it was still a
    card floating in the middle rather than a sheet on the bottom edge, ending
    85 pixels short of it at 834.

    Its own wh-modal rules are gone with it. The close button's stays, because the
    lookup panel elsewhere on this screen uses it.

    **Three buttons, where rule 34 asks for at most two.** Kept as it was, and
    said out loud rather than quietly trimmed: "Choose a different file" is a way
    back inside the dialog rather than a second act, and taking it away would
    mean closing and reopening to swap a file. Worth putting to the owner at the
    walkthrough.
  */
  return (
    <Dialog
      kind="info"
      icon={<IconDoc />}
      size="list"
      /* **Which company, in the title.** Job 14, 6 September 2026. A row's
         company comes from whichever one you are signed into, which is right,
         and having no confirmation anywhere is not: one import while pointed at
         the wrong site puts every location in the wrong place and nothing would
         say so. */
      title={'Import warehouses into ' + (current?.name || 'this company')}
      says="Takes the template from this page, or the Warehouse export straight out of Claimsoft. Nothing is saved until you say so."
      onClose={onClose}
      footer={
        <>
          {plan && (
            <button className="tb-btn tb-btn-ghost" onClick={() => { setPlan(null); setFileName(''); }}>
              Choose a different file
            </button>
          )}
          <span style={{ flex: 1 }} />
          <button className="tb-btn tb-btn-ghost" onClick={onClose}>Cancel</button>
          {plan && !nothingToDo && (
            <button className="tb-btn tb-btn-primary" onClick={apply} disabled={busy}>
              {busy ? 'Importing...' : `Import ${plan.creates.length + plan.updates.length} warehouses`}
            </button>
          )}
        </>
      }
    >
      <div className="wh-import-body">
          {!plan && !busy && (
            <>
              <button className="wh-drop" onClick={() => fileRef.current?.click()}>
                <div className="wh-drop-title">Choose a CSV file</div>
                <div className="wh-drop-note">
                  {fileName || 'Rows are matched on warehouse number, or on the name if there is no number.'}
                </div>
              </button>

              <div className="wh-note">
                Import referral sources <strong>before</strong> warehouses. A warehouse can only
                be paired with a referral source that already exists here.
              </div>

              <div>
                <div className="ua-section-title" style={{ marginBottom: 8 }}>Columns it reads</div>
                <div className="wh-cols-grid">
                  {WAREHOUSE_FIELDS.map(f => (
                    <div key={f.column} className="wh-col">
                      <span className="wh-col-name">
                        {f.column}
                        {f.alias?.length ? <span className="wh-col-alias">or {f.alias[0]}</span> : null}
                      </span>
                      {f.help && <span className="wh-col-help">{f.help}</span>}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {busy && !plan && <PageLoading />}
          <input ref={fileRef} type="file" accept=".csv,text/csv" style={{ display: 'none' }}
            onChange={e => pickFile(e.target.files?.[0])} />

          {error && <div className="wh-alert">{error}</div>}

          {plan && (
            <>
              <div className="wh-tally">
                <div className="wh-tally-box add">
                  <span className="wh-tally-n">{plan.creates.length}</span>
                  <span className="wh-tally-l">new warehouses</span>
                </div>
                <div className="wh-tally-box change">
                  <span className="wh-tally-n">{plan.updates.length}</span>
                  <span className="wh-tally-l">will be updated</span>
                </div>
                <div className="wh-tally-box">
                  <span className="wh-tally-n">{plan.unchanged.length}</span>
                  <span className="wh-tally-l">already correct</span>
                </div>
                <div className={'wh-tally-box' + (plan.problems.length ? ' warn' : '')}>
                  <span className="wh-tally-n">{plan.problems.length}</span>
                  <span className="wh-tally-l">names not found</span>
                </div>
              </div>

              {plan.repeats > 0 && (
                <div className="wh-note">
                  <strong>{plan.repeats.toLocaleString()}</strong> of the {plan.rowCount.toLocaleString()} rows
                  repeat a warehouse that is already earlier in the file, so they were skipped.
                  Claimsoft's export lists a warehouse once per bin, which is why the file is so much
                  longer than the number of warehouses in it.
                </div>
              )}

              {plan.ignored.length > 0 && (
                <div className="wh-note">
                  Columns in this file that OnHand does not use, left alone:{' '}
                  <strong>{plan.ignored.join(', ')}</strong>
                </div>
              )}

              {plan.problems.length > 0 && (
                <div className="wh-section">
                  <div className="ua-section-title">Names it could not find</div>
                  <div className="ua-section-note">
                    These cells are left empty rather than guessed at. Everything else on the row still imports.
                  </div>
                  <div className="wh-problems">
                    {summarise(plan.problems).slice(0, 60).map((p, i) => (
                      <div key={i} className="wh-problem">
                        <span className="wh-problem-field">{p.field}</span>
                        <span className="wh-problem-val">"{p.value}"</span>
                        <span className="wh-problem-note">
                          {p.note}{p.count > 1 ? `, ${p.count} rows` : `, row ${p.line}`}
                        </span>
                      </div>
                    ))}
                    {summarise(plan.problems).length > 60 && (
                      <div className="pick-empty">...and {summarise(plan.problems).length - 60} more.</div>
                    )}
                  </div>
                </div>
              )}

              {plan.updates.length > 0 && (
                <div className="wh-section">
                  <div className="ua-section-title">What changes on the ones already here</div>
                  <div className="wh-problems">
                    {plan.updates.slice(0, 40).map((u, i) => (
                      <div key={i} className="wh-problem">
                        <span className="wh-problem-field">{u.name}</span>
                        <span className="wh-problem-note">
                          {[
                            ...u.changes.map(labelFor),
                            u.hubName ? 'Primary Warehouse' : null,
                            u.assigned ? 'people assigned' : null,
                          ].filter(Boolean).join(', ')}
                        </span>
                      </div>
                    ))}
                    {plan.updates.length > 40 && (
                      <div className="pick-empty">...and {plan.updates.length - 40} more.</div>
                    )}
                  </div>
                </div>
              )}

              {nothingToDo && (
                <div className="wh-note">
                  Every row in this file already matches what is in OnHand. Nothing to do.
                </div>
              )}
            </>
          )}
      </div>
    </Dialog>
  );
}

const labelFor = key => WAREHOUSE_FIELDS.find(f => f.key === key)?.column || key;
